import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { UserVehicleApi } from '../apis/UserVehicleApi'
import { vehicleLabel } from '../utilities/generalUtils'

const inputRow = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '11px 14px',
    background: 'var(--gp-card)',
    border: '1px solid var(--gp-hairline)',
    borderRadius: 'var(--gp-r-md)',
}
const inputBase = {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    font: '400 14px/1 var(--gp-font)',
    color: 'var(--gp-ink)',
    width: '100%',
}
const labelStyle = {
    display: 'block',
    font: 'var(--gp-caption)',
    color: 'var(--gp-ink-mute)',
    marginBottom: 6,
}

const VehicleInfo = forwardRef(function VehicleInfo(
    { vehicle, handleCancel, parkingSpots, reloadVehicles },
    ref
) {
    const [nickname, setNickname] = useState(vehicle.name || '')
    const [parkingSpot, setParkingSpot] = useState(null)
    const [locationIndex, setLocationIndex] = useState(null)
    const locationCount = parkingSpots.length

    useEffect(() => {
        if (locationCount === 0) return
        if (!vehicle.parkingSpot) {
            if (locationCount === 1) setLocationIndex(0)
            return
        }
        const existingLocationIndex = parkingSpots.findIndex(
            (obj) => obj.uuid === vehicle.parkingSpot.locationUuid
        )
        setLocationIndex(existingLocationIndex)
        setParkingSpot(vehicle.parkingSpot.uuid)
    }, [vehicle, parkingSpots, locationCount])

    useImperativeHandle(ref, () => ({
        async save() {
            const next = { ...vehicle, soMappingUuid: parkingSpot, name: nickname }
            await UserVehicleApi.put(vehicle.uuid, next)
            reloadVehicles()
            handleCancel()
        },
        isValid: () => nickname.trim().length > 0,
    }))

    const plate = vehicleLabel(vehicle)

    return (
        <div>
            {/* Year / Make / Model surface card */}
            <div
                style={{
                    background: 'var(--gp-surface)',
                    border: '1px solid var(--gp-hairline)',
                    borderRadius: 'var(--gp-r-md)',
                    padding: '16px 18px',
                    marginBottom: 18,
                }}
            >
                <div
                    style={{
                        font: 'var(--gp-micro)',
                        color: 'var(--gp-ink-mute)',
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        marginBottom: 6,
                    }}
                >
                    Vehicle
                </div>
                <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                    {plate || '—'}
                </div>
            </div>

            {/* Nickname */}
            <label style={labelStyle}>Nickname</label>
            <div style={inputRow}>
                <input
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="e.g. Family car"
                    style={inputBase}
                />
            </div>

            {/* Parking location + space */}
            {locationCount > 0 && (
                <div style={{ marginTop: 16 }}>
                    <label style={labelStyle}>
                        {locationCount > 1 ? 'Parking location' : 'Parking space'}
                    </label>
                    {locationCount > 1 && (
                        <div style={{ ...inputRow, marginBottom: 12, padding: '8px 14px' }}>
                            <select
                                value={locationIndex ?? ''}
                                onChange={(e) => {
                                    setLocationIndex(Number(e.target.value))
                                    setParkingSpot(null)
                                }}
                                style={{
                                    ...inputBase,
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    cursor: 'pointer',
                                    paddingRight: 24,
                                }}
                            >
                                <option value="" disabled>
                                    Select a location
                                </option>
                                {parkingSpots.map((location, index) => (
                                    <option key={location.name} value={index}>
                                        {location.name}
                                    </option>
                                ))}
                            </select>
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.75"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ color: 'var(--gp-ink-mute)', flexShrink: 0 }}
                            >
                                <path d="M6 9l6 6 6-6" />
                            </svg>
                        </div>
                    )}
                    {locationIndex !== null && parkingSpots[locationIndex]?.spaces?.length > 0 && (
                        <div style={{ ...inputRow, padding: '8px 14px' }}>
                            <select
                                value={parkingSpot ?? ''}
                                onChange={(e) => setParkingSpot(e.target.value)}
                                style={{
                                    ...inputBase,
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    cursor: 'pointer',
                                    paddingRight: 24,
                                }}
                            >
                                <option value="" disabled>
                                    Select a space
                                </option>
                                {parkingSpots[locationIndex].spaces.map((spot) => (
                                    <option key={spot.name} value={spot.uuid}>
                                        {spot.name}
                                    </option>
                                ))}
                            </select>
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.75"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ color: 'var(--gp-ink-mute)', flexShrink: 0 }}
                            >
                                <path d="M6 9l6 6 6-6" />
                            </svg>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
})

export default VehicleInfo
